import { useCallback, useEffect, useState } from 'react';

import { Input, Skeleton } from '@storybook';
import './add-access-code.scss';
import { AdditionalFlag } from '../additional-flag';
import { IRecipient, IsEditViewOnly, ISignedDocInbox } from 'views/signed-doc-inbox';
import {
	IsRecipientLoading,
	useRecipients,
} from 'views/edit-recipient/stores';
import { useRecoilValue } from 'recoil';


interface IProps {
	recipient: IRecipient;
	index: number;
	inbox: ISignedDocInbox;
}

const ACCESS_CODE_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,50}$/;

const AddAccessCode = ({ recipient, index, inbox }: IProps) => {
	const [isAccessCodeEnabled, setIsAccessCodeEnabled] = useState(
		recipient.isAccessCodeEnabled || false
	);
	const [accessCode, setAccessCode] = useState(recipient.accessCode || '');
	const [isAccessCodeError, setIsAccessCodeError] = useState(false);

	const isRecipientLoading = useRecoilValue(IsRecipientLoading);
	const isEditViewOnly = useRecoilValue(IsEditViewOnly);
	const { setRecipientsWithValue } = useRecipients();

	const handleRegenerateAccessCode = useCallback(() => {
		function generateValidAccessCode() {
			const specials = '!@#$%^&*()_+-={}[]:;<>,.?/~';
			const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
			const digits = '0123456789';
			const allNonSpecials = letters + digits;

			const minLen = 8;
			const maxLen = 12;
			const len = Math.floor(Math.random() * (maxLen - minLen + 1)) + minLen;

			const code = [
				letters[Math.floor(Math.random() * letters.length)], // Ensure 1 letter
				digits[Math.floor(Math.random() * digits.length)], // Ensure 1 digit
				specials[Math.floor(Math.random() * specials.length)], // Exactly 1 special char
			];

			while (code.length < len) {
				code.push(
					allNonSpecials[Math.floor(Math.random() * allNonSpecials.length)]
				);
			}

			// Shuffle the characters
			for (let i = code.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[code[i], code[j]] = [code[j], code[i]];
			}

			return code.join('');
		}

		let newCode = '';
		do {
			newCode = generateValidAccessCode();
		} while (!ACCESS_CODE_REGEX.test(newCode));

		setAccessCode(newCode);
		setRecipientsWithValue('accessCode' as keyof IRecipient, newCode, index);
		setIsAccessCodeError(false);
	}, [index, setRecipientsWithValue]);

	const handleAccessCodeToggle = useCallback(
		(checked: boolean) => {
			setIsAccessCodeEnabled(checked);
			if (checked) {
				handleRegenerateAccessCode();
				setRecipientsWithValue('isAccessCodeEnabled', true, index);
				setIsAccessCodeError(false);
			} else {
				setAccessCode('');
				setRecipientsWithValue('isAccessCodeEnabled', false, index);
				setRecipientsWithValue('accessCode', undefined, index);
				setIsAccessCodeError(false);
			}
		},
		[handleRegenerateAccessCode, index, setRecipientsWithValue]
	);

	const handleAccessCodeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setAccessCode(value);
			setRecipientsWithValue('accessCode', value, index);
			setIsAccessCodeError(!ACCESS_CODE_REGEX.test(value));
		},
		[index, setRecipientsWithValue]
	);

	useEffect(() => {
		if (!isRecipientLoading) {
			setAccessCode(recipient?.accessCode || '');
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRecipientLoading, recipient?.accessCode]);

	return (
		<div className="add-recipient__access-code-container">
			<div className="add-recipient__access-code-body">
				<AdditionalFlag
					value={isAccessCodeEnabled}
					handleChange={handleAccessCodeToggle}
					heading={'Recipient Access Code'}
					isDisabled={inbox.authType !== 'facial'}
					isViewOnly={isEditViewOnly}
					description={
						'Recipient attempting to access will need to enter this code before accessing the document.'
					}
				/>
				{isAccessCodeEnabled && (
					<>
						{!isRecipientLoading ? !isEditViewOnly ? (
							
							<Input
								handleChange={handleAccessCodeChange}
								inputType="text"
								placeholder="Enter Access Code"
								label={
									<div className="add-recipient__access-code-regen">
										Access Code
										<span
											className="add-recipient__access-code-regen-btn"
											onClick={handleRegenerateAccessCode}
										>
											Regenerate New Access Code
										</span>
									</div>
								}
								value={accessCode}
								isError={isAccessCodeError}
								errorMessage={
									!accessCode?.trim()
										? 'Please enter access code'
										: accessCode.length < 8
										? 'Access code must be at least 8 characters'
										: accessCode.length > 50
										? 'Access code must be at most 50 characters'
										: !ACCESS_CODE_REGEX.test(accessCode)
										? 'Must contain at least 1 letter, 1 number, 1 special character'
										: ''
								}
								id={`${recipient._id}-accessCode`}
								autoComplete="off"
								inputName="accessCode"
							/>
						) :  <div className='add-recipient__access-code-view'>
							<p className='access-code-view-label'>Access Code</p> <span>:</span> <p className='access-code-view-value'>{accessCode}</p>
						</div> : (
							<Skeleton height={36} width={300} />
						)}

						{!isEditViewOnly && <div className="add-recipient__access-code-info">
							<i className="ri-information-line recipient__access-code-info-icon"></i>
							<p className="add-recipient__access-code-info-msg">
								Make sure to share this code securely with the signer or viewer.
								Without it, they won’t be able to proceed.
							</p>
						</div>}
					</>
				)}
			</div>
		</div>
	);
};

export default AddAccessCode;
